import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Save, UserRound } from "lucide-react";

const MAX_PROFILE_PICTURE_BYTES = 3 * 1024 * 1024;

const calculateAge = (birthday) => {
  if (!birthday) return null;
  const birthDate = new Date(`${birthday}T00:00:00`);
  if (Number.isNaN(birthDate.getTime()) || birthDate > new Date()) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  if (today.getMonth() < birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
};

const readImageFile = (file) => new Promise((resolve, reject) => {
  if (!file.type.startsWith("image/")) {
    reject(new Error("Choose an image file"));
    return;
  }
  if (file.size > MAX_PROFILE_PICTURE_BYTES) {
    reject(new Error("Photo must not exceed 3 MB"));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("Unable to read this photo"));
  reader.readAsDataURL(file);
});

export default function MyProfile() {
  const { user, updateUser } = useAuth();
  const [profileForm, setProfileForm] = useState({ name: "", short_name: "", phone: "", birthday: "", address: "" });
  const [profilePictureDraft, setProfilePictureDraft] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const age = calculateAge(profileForm.birthday);

  useEffect(() => {
    setProfileForm({
      name: user?.name || "",
      short_name: user?.short_name || "",
      phone: user?.phone || "",
      birthday: user?.birthday || "",
      address: user?.address || "",
    });
  }, [user]);

  const selectProfilePicture = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setProfilePictureDraft(await readImageFile(file));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      const payload = { ...profileForm };
      if (profilePictureDraft) payload.profile_picture = profilePictureDraft;
      const response = await api.put("/auth/profile", payload);
      updateUser(response.data);
      setProfilePictureDraft("");
      toast.success("Profile updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <header className="border-b border-border pb-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Account</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold">My profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">Update your contact details and profile photo.</p>
      </header>
      <form onSubmit={saveProfile} className="grid gap-5 border-t-4 border-sky-500 pt-4 md:grid-cols-[auto_1fr]">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
            {(profilePictureDraft || user?.profile_picture) ? (
              <img src={profilePictureDraft || user.profile_picture} alt={`${user?.name || "Your"} profile`} className="h-full w-full object-cover" />
            ) : (
              <UserRound className="h-10 w-10" />
            )}
          </div>
          <input id="my-profile-picture" type="file" accept="image/*" className="sr-only" onChange={selectProfilePicture} />
          <label htmlFor="my-profile-picture" className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-primary hover:underline">
            <Camera className="h-4 w-4" /> Choose photo
          </label>
          <p className="text-center text-xs text-muted-foreground">One photo, max 3 MB</p>
        </div>
        <div className="grid content-start gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="profile-name">Full name</Label>
            <Input id="profile-name" value={profileForm.name} onChange={(event) => setProfileForm({ ...profileForm, name: event.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="profile-short-name">Short name</Label>
            <Input id="profile-short-name" value={profileForm.short_name} onChange={(event) => setProfileForm({ ...profileForm, short_name: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="profile-phone">Contact number</Label>
            <Input id="profile-phone" type="tel" value={profileForm.phone} onChange={(event) => setProfileForm({ ...profileForm, phone: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="profile-birthday">Birthday</Label>
            <Input id="profile-birthday" type="date" value={profileForm.birthday} onChange={(event) => setProfileForm({ ...profileForm, birthday: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="profile-age">Age</Label>
            <output id="profile-age" className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-sm" aria-live="polite">
              {age === null ? "Not available" : `${age} ${age === 1 ? "year" : "years"}`}
            </output>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="profile-address">Address</Label>
            <Input id="profile-address" value={profileForm.address} onChange={(event) => setProfileForm({ ...profileForm, address: event.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={savingProfile}>
              <Save className="h-4 w-4" /> {savingProfile ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
